import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import momentTz from 'moment-timezone';
import { useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Spin,
  Table,
} from 'antd';

import {
  deleteResponses,
  exportResponses,
  getResponses,
} from '../../store/actions/responseActions';

import { editAnalytics } from '../../store/actions/analyticsActions';

import { upsellTrigger } from '../../store/actions/paymentActions';

import { formatTableHeaders, formatTableRows } from '../../utils/antdTableFormat';
import { getMaxResponseCount, getMaxUploadStorage, hasFeature } from '../../utils/restrictions';
import { DateRanges } from '../../constants/date-ranges';
import Loader from '../../components/Loader/Loader';

import './style.less';
import { ReloadOutlined, RiseOutlined, WhatsAppOutlined } from '@ant-design/icons';
import { CLOSE_SEND_TO_WHATSAPP_MODAL } from '../../store/types';
import { Controller, useForm } from 'react-hook-form';
import PhoneInputFormItem from '../../components/PhoneInput';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import generateURIText from '../../../../server/src/common/utils/generateURIText';

const { RangePicker } = DatePicker;

const Response = () => {
  const [dateRange, setDateRange] = useState({
    beg: moment().startOf('month'),
    end: moment().endOf('day'),
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);
  const [form, setForm] = useState({});
  const [analytics, setAnalytics] = useState({});

  const { isLoading: isUserLoading, me } = useSelector((state) => state.auth);
  const {
    isLoading,
    questions,
    responses,
    totalResponseCount,
    pageIndex,
    isExportLoading,
    openSendToWhatsAppModal,
  } = useSelector((state) => state.response);

  const { forms, isLoading: isFormsLoading } = useSelector((state) => state.form);
  const { items: analyticsItems } = useSelector((state) => state.analytics);

  const params = useParams();

  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    const item = forms.find((formObj) => formObj.id && formObj.id === params.id);
    if (item) setForm(item);
  }, [forms, params.id]);

  useEffect(() => {
    const item = analyticsItems.find((item) => form.id && item.form === form.id);
    if (item) setAnalytics(item);
  }, [analyticsItems, form.id]);

  useEffect(() => {
    if (analytics.unread > 0) {
      dispatch(editAnalytics(analytics.form, { unread: 0 }));
    }
  }, [analytics.form, analytics.unread, dispatch]);

  /**
   * Formats the response data to suit AntD table
   * structure.
   */
  const columns =
    questions.length > 0 ? formatTableHeaders(questions, form?.responseJumpType, t, dispatch) : [];
  const dataSource =
    responses.length > 0 ? formatTableRows(questions, responses, form?.responseJumpType) : [];

  /**
   * Let's fetch data every time one of the dependencies changes.
   */
  useEffect(() => {
    dispatch(
      getResponses(params.id, currentPage, pageSize, searchTerm, dateRange.beg, dateRange.end),
    );
  }, [dateRange, params, searchTerm, currentPage, pageSize, dispatch]);

  useEffect(() => {
    if (!hasFeature(me.plan, 'responses')) dispatch(upsellTrigger('responses'));
  }, [me, dispatch]);

  /**
   * What happens when the user moves to a different page.
   */
  function handleTableChange(pagination) {
    setPageSize(pagination.pageSize);
    setCurrentPage(pagination.current - 1);
  }

  function handleReload() {
    dispatch(getResponses(params.id, 0, pageSize, searchTerm, dateRange.beg, dateRange.end));
  }

  const handleExportResponses = () => {
    const browserTimeZone = momentTz.tz.guess();
    dispatch(exportResponses(params.id, searchTerm, dateRange.beg, dateRange.end, browserTimeZone));
  };

  const handleDeleteConfirm = () => {
    dispatch(
      deleteResponses(
        params.id,
        pageIndex,
        pageSize,
        searchTerm,
        dateRange.beg,
        dateRange.end,
        selectedRows,
      ),
    );
  };

  function handleSearchInputChange(e) {
    setSearchInput(e.target.value);
  }

  const debounceRef = useRef(0);
  useEffect(() => {
    if (searchInput === null || searchInput === undefined) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 1000);
  }, [searchInput]);

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  function onDateChange(date) {
    if (date) {
      setDateRange({
        beg: date[0].startOf('day'),
        end: date[1].endOf('day'),
      });
    } else {
      setDateRange({
        beg: moment().startOf('month'),
        end: moment().endOf('day'),
      });
    }
  }

  const rowSelectionConfig = {
    type: 'checkbox',
    selectedRowKeys: selectedRows,
    onChange: handleRowSelection,
    fixed: true,
    preserveSelectedRowKeys: false,
  };

  function handleRowSelection(selectedRowKeys) {
    setSelectedRows(selectedRowKeys);
  }

  useEffect(() => {
    setSelectedRows([]);
  }, [responses]);

  const isActionDisabled = !hasFeature(me.plan, 'responses');

  const sendToWhatsappResponse = responses?.find(
    (response) => response._id === openSendToWhatsAppModal,
  );

  const { plan, totalResponseCount: totalResponseCountStored = 0, totalUploadSize } = me || {};
  const maxUploadStorage = getMaxUploadStorage(plan);
  const maxResponseCount = getMaxResponseCount(plan);

  const responseCountPercent = (totalResponseCountStored / maxResponseCount) * 100;
  const uploadSizePercent = (totalUploadSize / 1000 / Math.round(maxUploadStorage / 1000)) * 100;

  const hasUploadQuestion = form?.formQuestions?.some((question) => question.type === 'FILE');

  return (
    <div className="Response">
      <div className="Response-table">
        {plan !== 'free' && (
          <>
            {responseCountPercent > 90 && (
              <Alert
                message={t('responses.response-count-alert')}
                showIcon
                // description="Info Description Info Description Info Description Info Description"
                type="warning"
                action={
                  <Button
                    // size="small"
                    type="primary"
                    onClick={() => history.push('/account')}
                    // icon={<RiseOutlined />}
                  >
                    View Usage
                  </Button>
                }
              />
            )}
            {hasUploadQuestion && uploadSizePercent > 90 && (
              <Alert
                message={t('responses.upload-storage-alert')}
                showIcon
                // description="Info Description Info Description Info Description Info Description"
                type="warning"
                action={
                  <Button
                    // size="small"
                    type="primary"
                    onClick={() => history.push('/account')}
                    // icon={<RiseOutlined />}
                  >
                    View Usage
                  </Button>
                }
              />
            )}
          </>
        )}
        <div className="table-actions-strip">
          <div className="table-actions-strip-left">
            <Spin spinning={isExportLoading}>
              <Button disabled={isActionDisabled} onClick={handleExportResponses}>
                {t('responses.export-as-csv')}
              </Button>
            </Spin>

            <Popconfirm
              placement="right"
              title={t('responses.confirm-delete')}
              okButtonProps={{ danger: true }}
              onConfirm={handleDeleteConfirm}
              okText={t('delete.ok', 'Delete')}
              cancelText={t('delete.cancel', 'Cancel')}
            >
              {' '}
              <Button danger disabled={selectedRows.length > 0 ? false : true}>
                {' '}
                {t('delete.ok', 'Delete')}{' '}
              </Button>
            </Popconfirm>
            <Button onClick={handleReload} icon={<ReloadOutlined />} disabled={isActionDisabled}>
              {t('reload', 'Reload')}
            </Button>
          </div>

          <div className="table-actions-strip-right">
            <Input
              value={searchInput}
              placeholder={t('search')}
              onChange={handleSearchInputChange}
              disabled={isActionDisabled}
            />
            <RangePicker
              disabled={isActionDisabled}
              size="small"
              allowClear={false}
              onChange={onDateChange}
              defaultValue={[dateRange.beg, dateRange.end]}
              ranges={DateRanges}
              disabledDate={(current) => {
                return (
                  (current && current > moment().endOf('day')) ||
                  current < moment('15-05-2020', 'DD-MM-YYYY') // started capturing this value from may 15 2020
                );
              }}
            />
          </div>
        </div>
        {isUserLoading ? (
          <Loader />
        ) : (
          <Table
            /* Pagination disabled in free plan as we are using *totalResponseCount* in the upselling message */
            pagination={
              me.plan !== 'free'
                ? { pageSize, total: totalResponseCount, current: pageIndex + 1 }
                : ''
            }
            bordered
            rowSelection={questions.length > 0 ? rowSelectionConfig : null}
            columns={columns}
            dataSource={dataSource}
            loading={isLoading || isFormsLoading}
            scroll={{ x: true }}
            onChange={handleTableChange}
            locale={{
              emptyText:
                me.plan === 'free' ? (
                  <Space direction="vertical" className="upsell-message">
                    <span className="plan-upgrade-prompt">{t('responses.upsell-message')}</span>
                    <Button
                      onClick={() => history.push('/billing')}
                      type="primary"
                      className="plan-upgrade-button"
                    >
                      {totalResponseCount === 0
                        ? t('upgrade')
                        : t('responses.upsell-button', { responseCount: totalResponseCount })}
                    </Button>

                    <span className="plan-upgrade-note">{t('responses.upsell-note')}</span>
                  </Space>
                ) : (
                  ''
                ),
            }}
          />
        )}
        <SendResponseToWhatsAppModal
          open={openSendToWhatsAppModal}
          onClose={() => dispatch({ type: CLOSE_SEND_TO_WHATSAPP_MODAL })}
          t={t}
          responseData={sendToWhatsappResponse}
          formData={form}
          key={sendToWhatsappResponse?._id}
        />
      </div>
    </div>
  );
};

const SendResponseToWhatsAppModal = ({ open, onClose, t, responseData, formData }) => {
  const { control, handleSubmit } = useForm({
    defaultValues: {
      whatsappNumber: responseData?.deliveredTo,
      // customMessage: '',
    },
  });

  const onSubmit = (data) => {
    const { whatsappNumber, customMessage } = data;

    const formatedValues = responseData.responses.reduce((acc, curr) => {
      acc[curr.questionId] = curr;
      return acc;
    }, {});

    const questionsMap = formData.formQuestions.reduce(
      (acc, curr) => ((acc[curr?.questionId] = curr), acc),
      {},
    );

    // build whatsapp message url
    let text = generateURIText(formData, formatedValues, questionsMap, responseData.responseNumber);

    // if (customMessage) {
    //   text += '\n────────────────\n\n' + customMessage;
    // }

    const url =
      'https://api.whatsapp.com/send?phone=' + whatsappNumber + '&text=' + encodeURIComponent(text);

    window.open(url, '_blank');
    onClose();
  };

  const onError = (error) => {
    console.log(error);
  };
  return (
    <Modal
      title={t('responses.send-to-whatsapp')}
      visible={open}
      footer={null}
      onCancel={onClose}
      className="send-to-whatsapp-modal"
    >
      <Form requiredMark={false} colon={false} labelCol={{ span: 7 }} labelAlign="left">
        <div style={{ padding: '24px' }}>
          <Controller
            render={({ field, fieldState: { error } }) => (
              <Form.Item label={t('responses.forward-to')} style={{ marginBottom: '0px' }}>
                <PhoneInputFormItem {...field} error={error} />
              </Form.Item>
            )}
            control={control}
            name="whatsappNumber"
            rules={{
              validate: (value) =>
                isPossiblePhoneNumber(`+${value}`) ? true : t('formSettings.enterValidNumber'),
            }}
          />
          {/* <Controller
            control={control}
            name="customMessage"
            render={({ field, fieldState: { error } }) => (
              <Form.Item label={t('responses.custom-message')}>
                <Input.TextArea {...field} showCount maxLength={500} style={{ height: 120 }} />
              </Form.Item>
            )}
          /> */}
        </div>
        <div className="ant-modal-footer">
          <Button type="button" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={() => handleSubmit(onSubmit, onError)()}
            icon={<WhatsAppOutlined />}
          >
            {t('responses.send-whatsapp')}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default Response;
